import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import API from "../lib/api";
import { FileCheck, ArrowRight, Eye, Pencil, EyeOff, Plus, Users, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Layers, ShieldAlert, ShieldCheck, ListFilter, FileSearch } from "lucide-react";

const EMPTY_FINDING_FORM = {
  control_id: "",
  control_description: "",
  finding_title: "",
  finding_description: "",
  suggested_severity: "Medium",
  final_severity: "",
  evidence_summary: "",
  regulator_clause_reference: "",
  policy_clause_reference: "",
  business_impact: "",
  root_cause: "",
  remediation_plan: "",
  owner_response: "",
  affected_references: [],
  affected_references_text: "",
  override_reason: "",
};

const parseReferenceList = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const SCORE_MULTIPLIER = 100;
const PREVIEW_REFERENCE_LIMIT = 3;
const HIGH_SEVERITY_THRESHOLD = "High";
const OBSERVATION_SEVERITY = "Observation";
const MODAL_ANOMALY_PREVIEW_LIMIT = 12;

const SUMMARY_CARD_CONFIG = {
  total: {
    label: "Open findings",
    icon: ListFilter,
    className: "border-slate-200/70 bg-white/80",
  },
  high: {
    label: "High severity",
    icon: ShieldAlert,
    className: "border-red-200/80 bg-red-50/80",
  },
  observation: {
    label: "Observations",
    icon: ShieldCheck,
    className: "border-blue-200/80 bg-blue-50/80",
  },
  evidence: {
    label: "Avg evidence",
    icon: FileSearch,
    className: "border-amber-200/80 bg-amber-50/80",
  },
};

const formatPercent = (value) => `${Math.round((Number(value) || 0) * SCORE_MULTIPLIER)}%`;

const fieldValue = (value, fallback) => {
  const text = Array.isArray(value) ? value.filter(Boolean).join("\n") : String(value || "").trim();
  return text || fallback;
};

const remediationPlanValue = (finding) =>
  fieldValue(
    finding?.remediation_plan || finding?.remediation_actions,
    "No remediation plan captured yet."
  );

const managementResponseValue = (finding) =>
  fieldValue(
    finding?.owner_response || finding?.management_response,
    "No management response captured yet."
  );

const summarizeAverage = (items, selector) => {
  if (!items.length) return 0;
  const total = items.reduce((sum, item) => sum + (Number(selector(item)) || 0), 0);
  return total / items.length;
};

const getAnomalyEntries = (finding) => {
  if (!Array.isArray(finding?.anomalous_items)) return [];

  return finding.anomalous_items.map((item, index) => {
    if (typeof item === "string") {
      return {
        id: `anomaly-${index}`,
        title: `Anomaly ${index + 1}`,
        preview: item,
        raw: item,
      };
    }

    if (item && typeof item === "object") {
      const entries = Object.entries(item);
      const firstPair = entries[0];
      return {
        id: item.id || item.restaurant_id || `anomaly-${index}`,
        title: item.restaurant_name || item.control_id || item.id || `Anomaly ${index + 1}`,
        preview: firstPair ? `${firstPair[0]}: ${String(firstPair[1])}` : JSON.stringify(item),
        raw: item,
      };
    }

    return {
      id: `anomaly-${index}`,
      title: `Anomaly ${index + 1}`,
      preview: String(item),
      raw: item,
    };
  });
};

const formatRawAnomaly = (raw) => {
  if (typeof raw === "string") return raw;
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
};

/**
 * Strip internal SQL audit debug fields from evidence_summary before display.
 * If the value is a JSON object produced by the SQL audit engine (contains keys
 * like normalized_sql / guard / execution / source_type) only the human-readable
 * violation count and explanation are returned.
 */
const sanitizeEvidenceSummary = (raw) => {
  if (!raw) return "";
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed && typeof parsed === "object") {
      const SQL_INTERNAL_KEYS = ["normalized_sql", "guard", "execution", "source_type"];
      if (SQL_INTERNAL_KEYS.some((k) => k in parsed)) {
        const parts = [];
        if (parsed.violations !== undefined) parts.push(`Violations detected: ${parsed.violations}`);
        if (parsed.explanation) parts.push(parsed.explanation);
        return parts.join("\n") || "Evidence captured.";
      }
    }
  } catch {
    // not JSON — fall through
  }
  return typeof raw === "string" ? raw : JSON.stringify(raw);
};

/**
 * Strip legacy "SQL audit PASS:" / "SQL audit FAIL:" prefixes that were
 * written into MongoDB by older versions of the SQL audit pipeline.
 * This keeps the UI clean for existing data without requiring re-evaluation.
 */
const sanitizeFindingTitle = (title) => {
  if (!title) return "";
  // Map old prefix → clean label
  const prefixMap = [
    { from: /^SQL audit FAIL:\s*/i, to: "Violation Detected: " },
    { from: /^SQL audit PASS:\s*/i, to: "Control Passed: " },
    { from: /^SQL audit\s+\w+:\s*/i, to: "" },  // any other SQL audit variant
  ];
  for (const { from, to } of prefixMap) {
    if (from.test(title)) return title.replace(from, to);
  }
  return title;
};

export default function Phase8Findings() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, dataVersion } = useAudit();
  const { hasPermission } = useAuth();
  const [findings, setFindings] = useState([]);
  const [traceView, setTraceView] = useState(null);
  const [detailsView, setDetailsView] = useState(null);
  const [editFinding, setEditFinding] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newFinding, setNewFinding] = useState(EMPTY_FINDING_FORM);
  const [continuing, setContinuing] = useState(false);

  // Team Mode state
  const [teamData, setTeamData] = useState(null);
  const [isTeamMode, setIsTeamMode] = useState(false);
  const [expandedTeamFinding, setExpandedTeamFinding] = useState(null);
  const [editTeamFinding, setEditTeamFinding] = useState(null);

  const fetchFindings = useCallback(async () => {
    if (!auditRun?.id) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}/findings`);
    setFindings(data);
  }, [auditRun?.id]);

  // Fetch team data if in team mode
  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData(data);
      setIsTeamMode(data.audit_mode === "team");
    } catch (e) {
      console.error("Failed to fetch team data:", e);
    }
  }, [auditRun?.id]);

  // Initial fetch and refresh when context dataVersion changes (from chat actions)
  useEffect(() => { fetchFindings(); fetchTeamData(); }, [fetchFindings, fetchTeamData, dataVersion]);

  const handleEdit = async () => {
    if (!editFinding) return;
    try {
      await API.put(`/audit-runs/${auditRun.id}/findings/${editFinding.id}`, {
        finding_title: editFinding.finding_title,
        finding_description: editFinding.finding_description,
        final_severity: editFinding.final_severity || editFinding.suggested_severity,
        override_reason: editFinding.override_reason || "",
        evidence_summary: editFinding.evidence_summary,
        policy_clause_reference: editFinding.policy_clause_reference,
        regulator_clause_reference: editFinding.regulator_clause_reference,
        business_impact: editFinding.business_impact,
        root_cause: editFinding.root_cause,
        remediation_plan: editFinding.remediation_plan,
        owner_response: editFinding.owner_response,
        affected_references: parseReferenceList(editFinding.affected_references_text),
      });
      setEditFinding(null);
      await fetchFindings();
      toast.success("Finding updated");
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleSuppress = async (id) => {
    try {
      await API.put(`/audit-runs/${auditRun.id}/findings/${id}`, { suppressed: true, override_reason: "Suppressed by auditor" });
      await fetchFindings();
      toast.success("Finding suppressed");
    } catch (e) { toast.error("Failed"); }
  };

  const handleAdd = async () => {
    try {
      await API.post(`/audit-runs/${auditRun.id}/findings`, newFinding);
      setAddOpen(false);
      setNewFinding(EMPTY_FINDING_FORM);
      await fetchFindings();
      toast.success("Manual finding added");
    } catch (e) { toast.error("Failed to add"); }
  };

  const handleContinue = () => {
    if (continuing) return;
    setContinuing(true);
    advancePhase(9);
    navigate("/phase/9", {
      state: {
        findings: activeFindings,
        teamData,
      },
    });
  };

  const handleTeamFindingEdit = async () => {
    if (!editTeamFinding) return;
    try {
      await API.put(`/audit-runs/${auditRun.id}/team/consolidated-finding/${editTeamFinding.id}`, {
        finding_title: editTeamFinding.finding_title,
        finding_description: editTeamFinding.finding_description,
        severity: editTeamFinding.severity,
        category: editTeamFinding.category,
        evidence_refs: parseReferenceList(editTeamFinding.evidence_refs_text),
        anomalous_items: parseReferenceList(editTeamFinding.anomalous_items_text),
        remediation_actions: parseReferenceList(editTeamFinding.remediation_actions_text),
        disputed: !!editTeamFinding.disputed,
        dispute_summary: editTeamFinding.dispute_summary,
        business_impact: editTeamFinding.business_impact,
        root_cause: editTeamFinding.root_cause,
        remediation_plan: editTeamFinding.remediation_plan,
        management_response: editTeamFinding.management_response,
        policy_reference: editTeamFinding.policy_reference,
        regulatory_reference: editTeamFinding.regulatory_reference,
      });
      setEditTeamFinding(null);
      await fetchTeamData();
      toast.success("Team finding updated");
    } catch (e) {
      toast.error("Failed to update team finding");
    }
  };

  const sevClass = (s) => ({
    "High": "severity-high", "Medium": "severity-medium", "Low": "severity-low", "Observation": "severity-observation"
  }[s] || "");

  const activeFindings = findings.filter(f => !f.suppressed);
  const consolidatedFindings = teamData?.consolidated_findings || [];
  const teamSummary = teamData?.team_executive_summary;
  const hasTeamFindings = consolidatedFindings.length > 0;
  const canEditFindings = hasPermission("finding:edit");
  const canRespondToFindings = hasPermission("finding:respond");
  const canSetSeverity = hasPermission("finding:severity");
  const canSuppressFindings = hasPermission("finding:suppress");

  // Severity badge styling
  const getSeverityBadge = (severity) => {
    const styles = {
      "High": "bg-red-500/10 text-red-500 border-red-500/30",
      "Medium": "bg-amber-500/10 text-amber-500 border-amber-500/30",
      "Low": "bg-blue-500/10 text-blue-500 border-blue-500/30",
      "Observation": "bg-slate-500/10 text-slate-400 border-slate-500/30"
    };
    return styles[severity] || styles["Medium"];
  };

  const findingsSummary = {
    total: activeFindings.length,
    high: activeFindings.filter((finding) => (finding.final_severity || finding.suggested_severity) === HIGH_SEVERITY_THRESHOLD).length,
    observation: activeFindings.filter((finding) => (finding.final_severity || finding.suggested_severity) === OBSERVATION_SEVERITY).length,
    evidence: formatPercent(summarizeAverage(activeFindings, (finding) => finding.evidence_sufficiency_score)),
  };

  return (
    <div className="space-y-6" data-testid="phase-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 8: Findings Draft</h2>
          <p className="text-sm text-muted-foreground mt-1">Review, refine, and document insurance audit findings before classification.</p>
        </div>
        <div className="flex gap-2">
          {isTeamMode && (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              <Users className="w-3 h-3 mr-1" /> Team Mode
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-finding-btn" disabled={!canEditFindings}>
            <Plus className="w-3 h-3 mr-1" /> Add Manual Finding
          </Button>
        </div>
      </div>

      {activeFindings.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {Object.entries(findingsSummary).map(([key, value]) => {
            const config = SUMMARY_CARD_CONFIG[key];
            const Icon = config.icon;

            return (
              <Card key={key} className={config.className}>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {config.label}
                    </p>
                    <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-background/80 shadow-sm">
                    <Icon className="h-5 w-5 text-foreground/70" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Team Consolidated Findings Section */}
      {isTeamMode && hasTeamFindings && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />
              <span>Team Consolidated Findings</span>
              <Badge variant="outline" className="ml-2 bg-emerald-500/10 text-emerald-400 text-xs">
                {consolidatedFindings.length} validated
              </Badge>
            </CardTitle>
            {teamSummary && (
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{teamSummary}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {consolidatedFindings.map((cf, idx) => (
              <div
                key={cf.id || idx}
                className="border rounded-lg bg-card transition-all"
                data-testid={`team-finding-${idx}`}
              >
                <div
                  className="p-4 cursor-pointer flex items-start justify-between gap-4"
                  onClick={() => setExpandedTeamFinding(expandedTeamFinding === idx ? null : idx)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`text-xs border ${getSeverityBadge(cf.severity)}`}>
                        {cf.severity}
                      </Badge>
                      {cf.disputed && (
                        <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Disputed
                        </Badge>
                      )}
                      {cf.status === 'validated' && (
                        <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" /> Validated
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-medium text-sm">{cf.finding_title}</h4>
  <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words">{cf.finding_description}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTeamFinding({
                          ...cf,
                          evidence_refs_text: (cf.evidence_refs || []).join(", "),
                          anomalous_items_text: (cf.anomalous_items || []).join(", "),
                          remediation_actions_text: (cf.remediation_actions || []).join(", "),
                          business_impact: cf.business_impact || "",
                          root_cause: cf.root_cause || "",
                          remediation_plan: cf.remediation_plan || (cf.remediation_actions || []).join("\n"),
                          management_response: cf.management_response || "",
                          policy_reference: cf.policy_reference || "",
                          regulatory_reference: cf.regulatory_reference || "",
                        });
                      }}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={(e) => { e.stopPropagation(); setDetailsView(cf); }}>
                      <Layers className="w-3 h-3 mr-1" /> Details
                    </Button>
                    {expandedTeamFinding === idx ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {expandedTeamFinding === idx && (
                  <div className="px-4 pb-4 pt-0 space-y-3 border-t border-border/50">
                    {/* Contributing Agents */}
                    {cf.contributing_agents?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground mb-1">Contributing Agents:</p>
                        <div className="flex flex-wrap gap-1">
                          {cf.contributing_agents.map((agent, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{agent}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evidence References */}
                    {cf.evidence_refs?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Evidence References:</p>
                        <div className="flex flex-wrap gap-1">
                          {cf.evidence_refs.map((ref, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-mono">{ref}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Remediation Actions */}
                    {cf.remediation_actions?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Recommended Remediation:</p>
                        <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
                          {cf.remediation_actions.map((action, i) => (
                            <li key={i}>{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Dispute Summary */}
                    {cf.disputed && cf.dispute_summary && (
                      <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30">
                        <p className="text-xs text-amber-400 font-medium mb-1">Dispute Summary:</p>
                        <p className="text-xs text-muted-foreground">{cf.dispute_summary}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Traditional Findings Table */}
      {activeFindings.length > 0 && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              {isTeamMode ? "Additional Solo Findings" : "Audit Findings"}
              <Badge variant="outline" className="ml-2 text-xs">{activeFindings.length}</Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Each finding is condensed into a scan-first card with key scores, references, and actions visible without opening raw detail text.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeFindings.map((f) => {
              const severity = f.final_severity || f.suggested_severity;

              return (
                <div
                  key={f.id}
                  data-testid={`finding-row-${f.id}`}
                  className="rounded-[1.25rem] border border-border/80 bg-white/70 p-4 shadow-sm backdrop-blur-sm transition-colors hover:border-border"
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono text-[10px]">{f.control_id || "Unmapped"}</Badge>
                        <Badge variant="outline" className={`text-xs border ${sevClass(severity)}`}>
                          {severity}
                        </Badge>
                        {f.manual && <Badge variant="outline" className="text-[10px]">Manual</Badge>}
                      </div>

                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold leading-6 whitespace-pre-wrap break-words">
                          {sanitizeFindingTitle(f.finding_title) || "Untitled finding"}
                        </h3>
                        <p className="text-sm leading-6 text-muted-foreground whitespace-pre-wrap break-words">
                          {f.finding_description || "No description captured for this finding yet."}
                        </p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-border/70 bg-background/80 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Evidence</p>
                          <p className="mt-2 font-mono text-lg font-semibold">{formatPercent(f.evidence_sufficiency_score)}</p>
                        </div>
                        <div className="rounded-2xl border border-border/70 bg-background/80 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Exposure</p>
                          <p className="mt-2 font-mono text-lg font-semibold">{formatPercent(f.regulatory_exposure_score)}</p>
                        </div>
                        <div className="rounded-2xl border border-border/70 bg-background/80 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">References</p>
                          <p className="mt-2 text-lg font-semibold">{f.affected_references?.length || 0}</p>
                        </div>
                      </div>

                      {!!f.affected_references?.length && (
                        <div className="flex flex-wrap gap-2">
                          {f.affected_references.slice(0, PREVIEW_REFERENCE_LIMIT).map((ref, index) => (
                            <Badge key={`${f.id}-ref-${index}`} variant="outline" className="max-w-full truncate font-mono text-[10px]">
                              {ref}
                            </Badge>
                          ))}
                          {f.affected_references.length > PREVIEW_REFERENCE_LIMIT && (
                            <Badge variant="outline" className="text-[10px]">
                              +{f.affected_references.length - PREVIEW_REFERENCE_LIMIT} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-wrap gap-2 xl:w-[220px] xl:justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-3"
                        title="Open Evidence Preview"
                        onClick={() => navigate("/phase/7-preview")}
                        data-testid={`evidence-preview-btn-${f.id}`}
                      >
                        <Eye className="mr-2 h-3.5 w-3.5" />
                        Evidence
                      </Button>
                      <Button variant="ghost" size="sm" className="h-9 px-3" title="Traceability" onClick={() => setTraceView(f)} data-testid={`trace-btn-${f.id}`}>
                        <Eye className="mr-2 h-3.5 w-3.5" />
                        Trace
                      </Button>
                      <Button variant="outline" size="sm" className="h-9 px-3" title="Details" onClick={() => setDetailsView(f)} data-testid={`details-btn-${f.id}`}>
                        <Layers className="mr-2 h-3.5 w-3.5" />
                        Details
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-3"
                        title="Edit"
                        disabled={!canEditFindings && !canRespondToFindings}
                        onClick={() =>
                          setEditFinding({
                            ...EMPTY_FINDING_FORM,
                            ...f,
                            override_reason: "",
                            affected_references_text: (f.affected_references || []).join(", "),
                          })
                        }
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="h-9 px-3 text-muted-foreground" title="Suppress" onClick={() => handleSuppress(f.id)} disabled={!canSuppressFindings}>
                        <EyeOff className="mr-2 h-3.5 w-3.5" />
                        Suppress
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!hasTeamFindings && activeFindings.length === 0 && (
        <Card>
          <CardContent className="min-h-[300px] py-8 px-6 text-center text-muted-foreground flex flex-col items-center justify-center">
            <FileCheck className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No findings generated yet.</p>
            <p className="text-xs mt-1">
              {isTeamMode
                ? "Run the team analysis on Phase 7 (Evidence) to generate consolidated findings."
                : "Run evidence evaluation to generate findings, or add findings manually."}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleContinue}
          disabled={continuing || (activeFindings.length === 0 && !hasTeamFindings)}
          data-testid="continue-to-classification-btn"
        >
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Classification
        </Button>
      </div>

      {/* Traceability View */}
      <Dialog open={!!traceView} onOpenChange={() => setTraceView(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[88vh] overflow-hidden">
          <DialogHeader><DialogTitle>Traceability View</DialogTitle></DialogHeader>
          {traceView && (
            <div className="space-y-3 text-sm max-h-[72vh] overflow-y-auto pr-2">
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Finding</p>
                <p className="leading-6 break-words">{traceView.finding_description}</p>
              </div>
              <div className="w-0.5 h-4 bg-border mx-auto" />
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Evidence</p>
                <div className="max-w-full overflow-x-auto rounded-xl bg-muted/30 p-3">
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6">
                    {sanitizeEvidenceSummary(traceView.evidence_summary) || "No evidence summary available"}
                  </pre>
                </div>
              </div>
              <div className="w-0.5 h-4 bg-border mx-auto" />
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Policy Clause</p>
                <p className="font-mono text-xs break-all">{traceView.policy_clause_reference || "N/A"}</p>
              </div>
              <div className="w-0.5 h-4 bg-border mx-auto" />
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Regulatory Clause</p>
                <p className="font-mono text-xs break-all">{traceView.regulator_clause_reference || "N/A"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Details View */}
      <Dialog open={!!detailsView} onOpenChange={() => setDetailsView(null)}>
          <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden">
          <DialogHeader><DialogTitle>Finding Detail Snapshot</DialogTitle></DialogHeader>
          {detailsView && (
            <div className="mt-2 max-h-[76vh] overflow-y-auto pr-2 text-sm">
              <div className="space-y-4">
                <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {(detailsView.control_id || detailsView.category) && (
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {detailsView.control_id || detailsView.category}
                      </Badge>
                    )}
                    {(detailsView.final_severity || detailsView.suggested_severity || detailsView.severity) && (
                      <Badge
                        variant="outline"
                        className={`text-xs border ${sevClass(detailsView.final_severity || detailsView.suggested_severity || detailsView.severity)}`}
                      >
                        {detailsView.final_severity || detailsView.suggested_severity || detailsView.severity}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Finding Context</p>
                  <p className="mt-2 text-base font-semibold leading-7">
                    {sanitizeFindingTitle(detailsView.finding_title) || (detailsView.finding_description && `${detailsView.finding_description.substring(0, 100)}...`) || "Untitled finding"}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {detailsView.finding_description || "No finding description captured."}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-border/80 p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Business Impact</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{fieldValue(detailsView.business_impact, "No business impact captured yet.")}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Root Cause</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{fieldValue(detailsView.root_cause, "No root cause captured yet.")}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Remediation Plan</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{remediationPlanValue(detailsView)}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Management Response</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{managementResponseValue(detailsView)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-border/80 p-4">
                    <Label className="block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Policy / Internal Reference</Label>
                    <p className="mt-2 break-words font-mono text-xs leading-6">
                      {detailsView.policy_clause_reference || detailsView.policy_reference || "N/A"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4">
                    <Label className="block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Regulatory Reference</Label>
                    <p className="mt-2 break-words font-mono text-xs leading-6">
                      {detailsView.regulator_clause_reference || detailsView.regulatory_reference || "N/A"}
                    </p>
                  </div>
                </div>

                {!!(detailsView.evidence_summary || detailsView.evidence_refs?.length) && (
                  <div className="rounded-2xl border border-border/80 p-4">
                    <Label className="block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Evidence Summary</Label>
                    <p className="mt-2 text-sm leading-6">
                      {sanitizeEvidenceSummary(detailsView.evidence_summary) || "Evidence references captured without a written summary."}
                    </p>
                    {!!detailsView.evidence_refs?.length && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {detailsView.evidence_refs.map((item, i) => (
                          <Badge key={i} variant="outline" className="font-mono text-xs">{item}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!!detailsView.affected_references?.length && (
                  <div>
                    <Label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Affected Policies / References</Label>
                    <div className="flex flex-wrap gap-2 rounded-2xl border border-border/80 bg-card p-4">
                      {detailsView.affected_references.map((item, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-xs">{item}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Identified Anomalies ({getAnomalyEntries(detailsView).length})
                  </Label>
                  {getAnomalyEntries(detailsView).length > 0 ? (
                    <div className="grid gap-3 lg:grid-cols-2">
                      {getAnomalyEntries(detailsView).slice(0, MODAL_ANOMALY_PREVIEW_LIMIT).map((item) => (
                        <div key={item.id} className="rounded-2xl border border-border/80 bg-card p-4">
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="mt-2 text-xs leading-5 text-muted-foreground">{item.preview}</p>
                          <pre className="mt-3 overflow-x-auto rounded-xl bg-muted/40 p-3 font-mono text-[11px] leading-5 text-foreground/80">
                            {formatRawAnomaly(item.raw)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed p-6 text-center">
                      <p className="text-sm text-muted-foreground">No specific anomalous items were extracted for this finding.</p>
                    </div>
                  )}
                  {getAnomalyEntries(detailsView).length > MODAL_ANOMALY_PREVIEW_LIMIT && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Showing the first {MODAL_ANOMALY_PREVIEW_LIMIT} anomalies to keep the modal readable.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editFinding} onOpenChange={() => setEditFinding(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden">
          <DialogHeader><DialogTitle>Edit Insurance Finding</DialogTitle></DialogHeader>
          {editFinding && (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Finding Title</Label>
                  <Input value={editFinding.finding_title || ""} onChange={e => setEditFinding(p => ({ ...p, finding_title: e.target.value }))} className="mt-1" placeholder="Example: Over-insurance beyond affordability threshold" disabled={!canEditFindings} /></div>
                <div><Label className="text-xs">Severity Override</Label>
                <Select value={editFinding.final_severity || editFinding.suggested_severity} onValueChange={v => setEditFinding(p => ({ ...p, final_severity: v }))} disabled={!canSetSeverity}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Observation">Observation</SelectItem>
                  </SelectContent>
                </Select></div>
              </div>
              <div><Label className="text-xs">Finding Description</Label>
                <Textarea value={editFinding.finding_description} onChange={e => setEditFinding(p => ({ ...p, finding_description: e.target.value }))} className="mt-1" rows={4} disabled={!canEditFindings} /></div>
              <div><Label className="text-xs">Evidence Summary</Label>
                <Textarea value={editFinding.evidence_summary || ""} onChange={e => setEditFinding(p => ({ ...p, evidence_summary: e.target.value }))} className="mt-1" rows={3} placeholder="Summarize the observed policy, underwriting, KYC, or approval evidence." disabled={!canEditFindings} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Policy / Internal Reference</Label>
                  <Input value={editFinding.policy_clause_reference || ""} onChange={e => setEditFinding(p => ({ ...p, policy_clause_reference: e.target.value }))} className="mt-1" placeholder="UW Guideline 4.2 / SOP-KYC-01" disabled={!canEditFindings} /></div>
                <div><Label className="text-xs">Regulatory Reference</Label>
                  <Input value={editFinding.regulator_clause_reference || ""} onChange={e => setEditFinding(p => ({ ...p, regulator_clause_reference: e.target.value }))} className="mt-1" placeholder="OJK Circular / internal policy clause" disabled={!canEditFindings} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Business Impact</Label>
                  <Textarea value={editFinding.business_impact || ""} onChange={e => setEditFinding(p => ({ ...p, business_impact: e.target.value }))} className="mt-1" rows={3} placeholder="Explain compliance, customer, underwriting, or financial impact." disabled={!canEditFindings} /></div>
                <div><Label className="text-xs">Root Cause</Label>
                  <Textarea value={editFinding.root_cause || ""} onChange={e => setEditFinding(p => ({ ...p, root_cause: e.target.value }))} className="mt-1" rows={3} placeholder="Document control breakdown, missing validation, approval gap, or process weakness." disabled={!canEditFindings} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Remediation Plan</Label>
                  <Textarea value={editFinding.remediation_plan || ""} onChange={e => setEditFinding(p => ({ ...p, remediation_plan: e.target.value }))} className="mt-1" rows={3} placeholder="Required remediation actions for underwriting, KYC, or exception governance." disabled={!canEditFindings} /></div>
                <div><Label className="text-xs">Management Response</Label>
                  <Textarea value={editFinding.owner_response || ""} onChange={e => setEditFinding(p => ({ ...p, owner_response: e.target.value }))} className="mt-1" rows={3} placeholder="Capture business owner response or agreed action." disabled={!canEditFindings && !canRespondToFindings} /></div>
              </div>
              <div><Label className="text-xs">Affected Policies / Applications / Exceptions</Label>
                <Input value={editFinding.affected_references_text || ""} onChange={e => setEditFinding(p => ({ ...p, affected_references_text: e.target.value }))} className="mt-1 font-mono text-xs" placeholder="POL-PRU-2026-1005, APP-2026-2041, EX-2000" disabled={!canEditFindings} /></div>
              <div><Label className="text-xs">Audit Override Rationale</Label>
                <Input value={editFinding.override_reason || ""} onChange={e => setEditFinding(p => ({ ...p, override_reason: e.target.value }))} className="mt-1" placeholder="Why was the wording or severity adjusted?" /></div>
              <Button onClick={handleEdit} className="w-full" data-testid="save-finding-edit-btn" disabled={!canEditFindings && !canRespondToFindings}>Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden">
          <DialogHeader><DialogTitle>Add Manual Finding</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Control ID</Label><Input value={newFinding.control_id} onChange={e => setNewFinding(p => ({ ...p, control_id: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Severity</Label>
                <Select value={newFinding.suggested_severity} onValueChange={v => setNewFinding(p => ({ ...p, suggested_severity: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem><SelectItem value="Observation">Observation</SelectItem>
                  </SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Finding Title</Label>
              <Input value={newFinding.finding_title} onChange={e => setNewFinding(p => ({ ...p, finding_title: e.target.value }))} className="mt-1" placeholder="Example: Unauthorized underwriting exception approval" /></div>
            <div><Label className="text-xs">Finding Description</Label>
              <Textarea value={newFinding.finding_description} onChange={e => setNewFinding(p => ({ ...p, finding_description: e.target.value }))} className="mt-1" rows={3} /></div>
            <div><Label className="text-xs">Evidence Summary</Label>
              <Textarea value={newFinding.evidence_summary} onChange={e => setNewFinding(p => ({ ...p, evidence_summary: e.target.value }))} className="mt-1" rows={3} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Business Impact</Label>
                <Textarea value={newFinding.business_impact} onChange={e => setNewFinding(p => ({ ...p, business_impact: e.target.value }))} className="mt-1" rows={3} /></div>
              <div><Label className="text-xs">Root Cause</Label>
                <Textarea value={newFinding.root_cause} onChange={e => setNewFinding(p => ({ ...p, root_cause: e.target.value }))} className="mt-1" rows={3} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Remediation Plan</Label>
                <Textarea value={newFinding.remediation_plan} onChange={e => setNewFinding(p => ({ ...p, remediation_plan: e.target.value }))} className="mt-1" rows={3} /></div>
              <div><Label className="text-xs">Management Response</Label>
                <Textarea value={newFinding.owner_response} onChange={e => setNewFinding(p => ({ ...p, owner_response: e.target.value }))} className="mt-1" rows={3} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Policy / Internal Reference</Label><Input value={newFinding.policy_clause_reference} onChange={e => setNewFinding(p => ({ ...p, policy_clause_reference: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Regulatory Reference</Label><Input value={newFinding.regulator_clause_reference} onChange={e => setNewFinding(p => ({ ...p, regulator_clause_reference: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Affected Policies / Applications / Exceptions</Label>
              <Input value={newFinding.affected_references_text} onChange={e => setNewFinding(p => ({ ...p, affected_references_text: e.target.value, affected_references: parseReferenceList(e.target.value) }))} className="mt-1 font-mono text-xs" placeholder="POL-PRU-2026-1005, APP-2026-2041, EX-2000" /></div>
            <Button onClick={handleAdd} className="w-full" data-testid="confirm-add-finding-btn" disabled={!canEditFindings}>Add Finding</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTeamFinding} onOpenChange={() => setEditTeamFinding(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden">
          <DialogHeader><DialogTitle>Edit Consolidated Finding</DialogTitle></DialogHeader>
          {editTeamFinding && (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Finding Title</Label>
                  <Input value={editTeamFinding.finding_title || ""} onChange={e => setEditTeamFinding(p => ({ ...p, finding_title: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs">Severity</Label>
                  <Select value={editTeamFinding.severity || "Medium"} onValueChange={v => setEditTeamFinding(p => ({ ...p, severity: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Observation">Observation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Finding Description</Label>
                <Textarea value={editTeamFinding.finding_description || ""} onChange={e => setEditTeamFinding(p => ({ ...p, finding_description: e.target.value }))} className="mt-1" rows={4} /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Business Impact</Label>
                  <Textarea value={editTeamFinding.business_impact || ""} onChange={e => setEditTeamFinding(p => ({ ...p, business_impact: e.target.value }))} className="mt-1" rows={3} /></div>
                <div><Label className="text-xs">Root Cause</Label>
                  <Textarea value={editTeamFinding.root_cause || ""} onChange={e => setEditTeamFinding(p => ({ ...p, root_cause: e.target.value }))} className="mt-1" rows={3} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Policy Reference</Label>
                  <Input value={editTeamFinding.policy_reference || ""} onChange={e => setEditTeamFinding(p => ({ ...p, policy_reference: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs">Regulatory Reference</Label>
                  <Input value={editTeamFinding.regulatory_reference || ""} onChange={e => setEditTeamFinding(p => ({ ...p, regulatory_reference: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Evidence References</Label>
                <Input value={editTeamFinding.evidence_refs_text || ""} onChange={e => setEditTeamFinding(p => ({ ...p, evidence_refs_text: e.target.value }))} className="mt-1 font-mono text-xs" placeholder="POL-PRU-2026-1005, APP-2026-2003" /></div>
              <div><Label className="text-xs">Anomalous Items</Label>
                <Textarea value={editTeamFinding.anomalous_items_text || ""} onChange={e => setEditTeamFinding(p => ({ ...p, anomalous_items_text: e.target.value }))} className="mt-1 font-mono text-xs" rows={3} placeholder="Comma-separated exact policy / exception / row references" /></div>
              <div><Label className="text-xs">Remediation Actions</Label>
                <Textarea value={editTeamFinding.remediation_actions_text || ""} onChange={e => setEditTeamFinding(p => ({ ...p, remediation_actions_text: e.target.value }))} className="mt-1" rows={3} placeholder="Comma-separated remediation actions" /></div>
              <div><Label className="text-xs">Remediation Plan</Label>
                <Textarea value={editTeamFinding.remediation_plan || ""} onChange={e => setEditTeamFinding(p => ({ ...p, remediation_plan: e.target.value }))} className="mt-1" rows={3} /></div>
              <div><Label className="text-xs">Management Response</Label>
                <Textarea value={editTeamFinding.management_response || ""} onChange={e => setEditTeamFinding(p => ({ ...p, management_response: e.target.value }))} className="mt-1" rows={3} /></div>
              <Button onClick={handleTeamFindingEdit} className="w-full">Save Consolidated Finding</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
